import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  Users,
  Target,
  Download,
  Calendar
} from 'lucide-react';
import {
  getDashboardAnalytics,
  exportAnalyticsData
} from '../services/analyticsService';
import { usePermissions } from '../hooks/usePermissions';
import MetricCard from './charts/MetricCard';
import SimpleBarChart from './charts/SimpleBarChart';
import SimplePieChart from './charts/SimplePieChart';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';

const AnalyticsPanel = () => {
  const { canExportAnalytics } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const [analyticsData, setAnalyticsData] = useState({
    proposals: {
      totalProposals: 0,
      submitted: 0,
      clientResponses: 0,
      successfulOutcomes: 0,
      successRate: 0,
      responseRate: 0
    },
    relevance: {
      relevant: 0,
      nonRelevant: 0,
      skillMatches: []
    },
    responses: {
      totalResponses: 0,
      successful: 0,
      failed: 0
    },
    submissions: {
      daily: [],
      weekly: [],
      monthly: []
    }
  });

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };

      const dashboardData = await getDashboardAnalytics(params);

      setAnalyticsData({
        proposals: dashboardData.proposals || analyticsData.proposals,
        relevance: dashboardData.relevance || analyticsData.relevance,
        responses: dashboardData.responses || analyticsData.responses,
        submissions: dashboardData.submissions || analyticsData.submissions
      });
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const handleExport = async (type) => {
    try {
      await exportAnalyticsData({
        type,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
    } catch (err) {
      console.error('Export failed:', err);
      setError('Export failed: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminPageLayout>
      <AdminCard bodyClassName="space-y-6">
      {/* Header with filters and export */}
      <div className="admin-filter-panel flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="admin-filter-control"
            />
            <span className="text-sm text-gray-500">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="admin-filter-control"
            />
          </div>
        </div>

        {canExportAnalytics() && (
          <button
            onClick={() => handleExport('proposals')}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Download className="h-3.5 w-3.5" />
            Export Data
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          title="Total Proposals"
          value={analyticsData.proposals.totalProposals}
          icon={BarChart3}
          color="blue"
        />
        <MetricCard
          title="Success Rate"
          value={`${analyticsData.proposals.successRate}%`}
          icon={Target}
          color="green"
        />
        <MetricCard
          title="Response Rate"
          value={`${analyticsData.proposals.responseRate}%`}
          icon={Users}
          color="purple"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SimplePieChart
          title="Job Relevance Analysis"
          data={[
            { label: 'Relevant Jobs', value: analyticsData.relevance.relevant },
            { label: 'Non-Relevant Jobs', value: analyticsData.relevance.nonRelevant }
          ]}
          colors={['#10B981', '#EF4444']}
        />

        <SimplePieChart
          title="Client Response Outcomes"
          data={[
            { label: 'Successful', value: analyticsData.responses.successful },
            { label: 'Failed/No Response', value: analyticsData.responses.failed }
          ]}
          colors={['#10B981', '#EF4444']}
        />

        <SimpleBarChart
          title="Top Skill Matches"
          data={analyticsData.relevance.skillMatches.map(item => ({
            label: item.skill,
            value: item.matches
          }))}
        />

        <SimpleBarChart
          title="Proposal Trends"
          data={(analyticsData.submissions.daily || []).map(item => ({
            label: new Date(item.date).toLocaleDateString(),
            value: item.count
          }))}
        />
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Proposal Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Submitted</span>
              <span className="font-medium">{analyticsData.proposals.submitted}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Client Responses</span>
              <span className="font-medium">{analyticsData.proposals.clientResponses}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Successful Outcomes</span>
              <span className="font-medium">{analyticsData.proposals.successfulOutcomes}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Response Analysis</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Responses</span>
              <span className="font-medium">{analyticsData.responses.totalResponses}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Success Rate</span>
              <span className="font-medium text-green-600">
                {analyticsData.responses.totalResponses > 0
                  ? ((analyticsData.responses.successful / analyticsData.responses.totalResponses) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Insights</h3>
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-blue-800">
                <strong>Top Skill:</strong> {analyticsData.relevance.skillMatches[0]?.skill || 'N/A'}
                {' '}({analyticsData.relevance.skillMatches[0]?.matches || 0} matches)
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-green-800">
                <strong>Success Rate:</strong> {analyticsData.proposals.successRate}%
                {' '}({analyticsData.proposals.successfulOutcomes} successful)
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <p className="text-yellow-800">
                <strong>Relevance:</strong> {(analyticsData.relevance.relevant + analyticsData.relevance.nonRelevant) > 0
                  ? ((analyticsData.relevance.relevant / (analyticsData.relevance.relevant + analyticsData.relevance.nonRelevant)) * 100).toFixed(1)
                  : 0}% of jobs are relevant
              </p>
            </div>
          </div>
        </div>
      </div>
      </AdminCard>
    </AdminPageLayout>
  );
};

export default AnalyticsPanel;
